import { useEffect, useRef, useState } from "react";

// ─── Hooks ────────────────────────────────────────────────────────────────
function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function CountUp({ end, suffix = "", duration = 2200 }) {
  const [val, setVal] = useState(0);
  const [ref, inView] = useInView(0.3);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = Math.ceil(end / (duration / 16));
    const t = setInterval(() => {
      start += step;
      if (start >= end) { start = end; clearInterval(t); }
      setVal(start);
    }, 16);
    return () => clearInterval(t);
  }, [inView, end, duration]);
  return <span ref={ref}>{val}{suffix}</span>;
}

function Section({ id, children, className = "", bg = "bg-white" }) {
  const [ref, inView] = useInView(0.08);
  return (
    <section
      id={id}
      ref={ref}
      className={`relative overflow-hidden transition-all duration-800 ease-out ${inView ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"} ${bg} ${className}`}
    >
      {children}
    </section>
  );
}

// ─── Icons ────────────────────────────────────────────────────────────────
function IconDownload({ className = "w-5 h-5" }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

function IconArrowDown({ className = "w-5 h-5" }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
    </svg>
  );
}

function IconExternal({ className = "w-5 h-5" }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
    </svg>
  );
}

// ─── SECTION LABEL ────────────────────────────────────────────────────────
function SectionLabel({ children }) {
  return (
    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium tracking-wide" style={{ background: "#FEF7F4", color: "#DA2128" }}>
      <span className="relative flex w-1.5 h-1.5">
        <span className="absolute inline-flex w-full h-full rounded-full bg-[#DA2128] opacity-75 animate-ping" style={{ animationDuration: "2s" }} />
        <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-[#DA2128]" />
      </span>
      {children}
    </div>
  );
}

// ─── DECORATIVE ORB ───────────────────────────────────────────────────────
function Orb({ color, size, top, left, opacity = "0.05" }) {
  return (
    <div
      className="absolute rounded-full pointer-events-none"
      style={{
        width: size, height: size, top, left,
        background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
        opacity,
        transform: "translate(-50%, -50%)",
      }}
    />
  );
}


function Breadcrumb() {
  return (
    <nav
      aria-label="Breadcrumb"
      className="breadcrumb-in absolute left-4 top-4 z-20 sm:left-8 sm:top-6"
    >
      <ol className="flex items-center gap-2 rounded-full border border-black/10 bg-white/70 px-4 py-2 text-xs font-bold uppercase tracking-wide text-[#141419] shadow-lg shadow-black/5 backdrop-blur-md sm:text-sm">
        <li>
          <a
            href="#top"
            className="inline-flex items-center gap-1.5 transition-colors duration-300 hover:text-[#DA2128]"
          >
            <svg
              viewBox="0 0 24 24"
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 10.5 12 3l9 7.5" />
              <path d="M5 10v10h14V10" />
              <path d="M9 20v-6h6v6" />
            </svg>
            Home
          </a>
        </li>

        <li className="text-black/30">&rsaquo;</li>
        <li className="rounded-full bg-[#DA2128] px-3 py-1 text-white shadow-sm">AFCSR</li>
      </ol>
    </nav>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  HERO
// ═══════════════════════════════════════════════════════════════════════════
function Hero() {
  const [ref, inView] = useInView(0.08);
  return (
    <section
      ref={ref}
      className="relative min-h-screen flex items-center pt-[68px] overflow-hidden"
      style={{ background: "linear-gradient(160deg, #0f0f1a 0%, #0a0a15 40%, #1a1a2e 70%, #0f0f1a 100%)" }}
    >
        <Breadcrumb />
      {/* Abstract background image */}
      <div className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url(/images/hero-bg.jpg)" }}
      />
      {/* Gradient overlay on image — lets abstract shapes show through */}
      <div className="absolute inset-0"
        style={{ background: "linear-gradient(160deg, rgba(15,15,26,0.78) 0%, rgba(10,10,21,0.55) 35%, rgba(26,26,46,0.6) 65%, rgba(15,15,26,0.85) 100%)" }}
      />
      
      <Orb color="#DA2128" size="600px" top="20%" left="-10%" opacity="0.08" />
      <Orb color="#F59C00" size="400px" top="60%" left="80%" opacity="0.06" />
      <Orb color="#DA2128" size="300px" top="80%" left="30%" opacity="0.05" />
      
      <div className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: "60px 60px",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-8">
        <div className={`grid lg:grid-cols-2 gap-12 items-center transition-all duration-1000 ease-out ${inView ? "translate-y-0 opacity-100" : "translate-y-16 opacity-0"}`}>
          {/* Left */}
          <div className="space-y-5">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-medium text-white shadow-lg" style={{ background: "linear-gradient(135deg, rgba(218,33,40,0.85), rgba(219,100,0,0.85))", backdropFilter: "blur(10px)" }}>
              <span className="relative flex w-1.5 h-1.5">
                <span className="animate-ping absolute inline-flex w-full h-full rounded-full bg-white opacity-75" style={{ animationDuration: "2.5s" }} />
                <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-white" />
              </span>
              Established 2013 · Social Arm of ASSOCHAM
            </div>
            
            <h1 className="font-fraunces text-[clamp(1.6rem,4vw,2.8rem)] font-[600] leading-[1.1] tracking-tight">
              <span className="text-white">ASSOCHAM Foundation</span>
              <br />
              <span className="text-transparent bg-clip-text" style={{ backgroundImage: "linear-gradient(135deg, #F59C00, #DA2128, #DB6400)" }}>
                for Corporate Social Responsibility
              </span>
            </h1>
            
            <p className="text-sm sm:text-base leading-relaxed max-w-xl" style={{ color: "rgba(255,255,255,0.65)" }}>
              Rooted in the legacy of ASSOCHAM, India&apos;s Apex Chamber of Commerce, AFCSR bridges
              the gap between public policy and grassroots action — delivering impactful programmes
              in partnership with corporates, government bodies, development organisations, and communities.
            </p>

            <div className="flex flex-wrap gap-3 pt-1">
              <a
                href="#pillars"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-semibold text-white shadow-2xl transition-all duration-300 hover:scale-[1.04] hover:shadow-[0_8px_30px_rgba(218,33,40,0.4)] active:scale-[0.98]"
                style={{ background: "linear-gradient(135deg, #DA2128, #DB6400)" }}
              >
                Explore Our Pillars
                <IconArrowDown className="w-3.5 h-3.5" />
              </a>
              <a
                href="https://assocham.org/uploads/AFCSR_Brochure_2026.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-semibold border transition-all duration-300 hover:scale-[1.04] active:scale-[0.98]"
                style={{ borderColor: "rgba(255,255,255,0.25)", color: "rgba(255,255,255,0.9)" }}
              >
                <IconDownload className="w-3.5 h-3.5" />
                Download Brochure
              </a>
            </div>

            {/* Stats */}
            <div className="flex gap-6 sm:gap-10 pt-3" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
              {[
                { label: "States Reached", end: 22, suffix: "+" },
                { label: "Lives Impacted", end: 10, suffix: "L+" },
                { label: "Active Partners", end: 50, suffix: "+" },
              ].map((s) => (
                <div key={s.label}>
                  <p className="text-xl sm:text-2xl font-bold font-fraunces text-transparent bg-clip-text" style={{ backgroundImage: "linear-gradient(135deg, #F59C00, #DA2128)" }}>
                    <CountUp end={s.end} suffix={s.suffix} />
                  </p>
                  <p className="text-[10px] sm:text-xs mt-0.5" style={{ color: "rgba(255,255,255,0.5)" }}>{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Premium Visual */}
          <div className="hidden lg:flex justify-center">
            <div className="relative w-full max-w-sm">
              <div className="relative bg-white/5 backdrop-blur-xl rounded-3xl shadow-2xl overflow-hidden border border-white/10 animate-premise-float">
                <div className="h-[2px] animate-shimmer" style={{ background: "linear-gradient(90deg, #DA2128, #F59C00, #DB6400, #DA2128)", backgroundSize: "200% 100%" }} />
                <div className="p-6 space-y-4">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg" style={{ background: "linear-gradient(135deg, rgba(218,33,40,0.8), rgba(219,100,0,0.8))" }}>
                    <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0012 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white font-fraunces">Viksit Bharat 2047</h3>
                    <p className="mt-1.5 leading-relaxed text-xs" style={{ color: "rgba(255,255,255,0.6)" }}>
                      Contributing to India&apos;s vision of a developed nation through sustainable and inclusive development initiatives.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {["Education", "Health", "Skilling", "Climate"].map((tag) => (
                      <span key={tag} className="px-2.5 py-1 rounded-full text-[10px] font-medium text-white/90" style={{ background: "rgba(218,33,40,0.3)", backdropFilter: "blur(4px)" }}>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 w-20 h-20 rounded-full border border-white/10" />
              <div className="absolute -bottom-3 -left-3 w-16 h-16 rounded-full" style={{ background: "radial-gradient(circle, rgba(218,33,40,0.15), transparent)" }} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  OVERVIEW
// ═══════════════════════════════════════════════════════════════════════════
function Overview() {
  const items = [
    { num: "2013", label: "Year Established", icon: "M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" },
    { num: "5", label: "Thematic Pillars", icon: "M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5m.75-9l3-3 2.148 2.148A12.061 12.061 0 0116.5 7.605" },
    { num: "22+", label: "States Covered", icon: "M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z" },
    { num: "100+", label: "Programmes Delivered", icon: "M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" },
  ];

  return (
    <Section id="overview" bg="bg-white">
      <Orb color="#DA2128" size="500px" top="50%" left="90%" opacity="0.03" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="space-y-5">
            <SectionLabel>Foundation Overview</SectionLabel>
            <h2 className="font-fraunces text-[clamp(1.4rem,3vw,2.2rem)] font-[600] leading-[1.15] text-gray-900">
              Bridging Policy with{" "}
              <span className="text-transparent bg-clip-text" style={{ backgroundImage: "linear-gradient(135deg, #DA2128, #DB6400)" }}>Grassroots Action</span>
            </h2>
            <div className="space-y-3 text-sm text-gray-600 leading-relaxed">
              <p>
                The <strong className="text-gray-900">ASSOCHAM Foundation for Corporate Social Responsibility (AFCSR)</strong> was established as a Trust in 2013 to bridge the gap between public policy and grassroots action, delivering impactful programmes in partnership with corporates, government bodies, development organisations, and communities.
              </p>
              <p>
                AFCSR is dedicated to identifying critical social, developmental, and environmental challenges that require urgent attention, measurable outcomes, and sustainable solutions, contributing to the vision of <strong className="text-gray-900">Viksit Bharat 2047</strong>.
              </p>
              <p>
                As the social arm of ASSOCHAM, AFCSR plays a pivotal role in advancing inclusive and sustainable development through well-designed programmes and strategic partnerships.
              </p>
            </div>
            <a
              href="https://assocham.org/uploads/AFCSR_Brochure_2026.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-semibold text-white shadow-lg transition-all duration-300 hover:scale-[1.04] hover:shadow-xl active:scale-[0.98]"
              style={{ background: "linear-gradient(135deg, #DA2128 0%, #DB6400 100%)" }}
            >
              <IconDownload className="w-4 h-4" />
              Download Detailed Brochure
            </a>
          </div>

          <div className="relative">
            <div className="grid grid-cols-2 gap-4">
              {items.map((item) => (
                <div
                  key={item.label}
                  className="group relative bg-[#FAF7F2] rounded-2xl p-5 transition-all duration-400 hover:shadow-xl hover:-translate-y-1.5 hover:bg-white"
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 transition-all duration-300 group-hover:scale-110" style={{ background: "#FEF7F4" }}>
                    <svg className="w-5 h-5 text-[#DA2128]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
                    </svg>
                  </div>
                  <p className="text-xl sm:text-2xl font-bold font-fraunces text-[#DA2128]">{item.num}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  VISION & MISSION
// ═══════════════════════════════════════════════════════════════════════════
function VisionMission() {
  return (
    <Section id="vision" bg="bg-[#FAF7F2]">
      <Orb color="#F59C00" size="400px" top="20%" left="20%" opacity="0.04" />
      <Orb color="#DA2128" size="350px" top="70%" left="80%" opacity="0.03" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="text-center mb-12">
          <SectionLabel>Our Vision &amp; Our Mission</SectionLabel>
          <h2 className="font-fraunces text-[clamp(1.4rem,3vw,2.2rem)] font-[600] leading-[1.15] text-gray-900 mt-4">
            Driving <span className="text-transparent bg-clip-text" style={{ backgroundImage: "linear-gradient(135deg, #DA2128, #DB6400)" }}>Sustainable Transformation</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {/* Vision */}
          <div className="group relative bg-white rounded-[1.75rem] p-6 lg:p-8 shadow-xl transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl overflow-hidden">
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-[0.04] transition-all duration-500 group-hover:opacity-[0.08] group-hover:scale-110" style={{ background: "radial-gradient(circle, #DA2128 0%, transparent 70%)" }} />
            <div className="relative">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4 shadow-md" style={{ background: "linear-gradient(135deg, #DA2128, #DB6400)" }}>
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
                </svg>
              </div>
              <h3 className="font-fraunces text-lg font-[600] text-[#DA2128] mb-3">Our Vision</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                To be a catalyst for sustainable transformation by fostering collaboration between industry, government, and communities.
              </p>
            </div>
          </div>

          {/* Mission */}
          <div className="group relative bg-white rounded-[1.75rem] p-6 lg:p-8 shadow-xl transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl overflow-hidden">
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-[0.04] transition-all duration-500 group-hover:opacity-[0.08] group-hover:scale-110" style={{ background: "radial-gradient(circle, #F59C00 0%, transparent 70%)" }} />
            <div className="relative">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4 shadow-md" style={{ background: "linear-gradient(135deg, #F59C00, #DB6400)" }}>
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                </svg>
              </div>
              <h3 className="font-fraunces text-lg font-[600] text-[#DB6400] mb-3">Our Mission</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                To drive impactful CSR through policy advocacy, meaningful public-private partnerships, sector recognition, and knowledge creation.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  THEMATIC PILLARS
// ═══════════════════════════════════════════════════════════════════════════
const pillars = [
  {
    title: "Education",
    subtitle: "Quality & Experiential Learning",
    color: "#DA2128",
    items: ["Improving access to quality education", "Life-skills & hygiene awareness", "Road safety & climate education", "Experiential learning for children & adolescents"],
    icon: "M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.627 48.627 0 0 1 12 20.904a48.627 48.627 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.57 50.57 0 0 0-2.658-.813A59.905 59.905 0 0 1 12 3.493a59.902 59.902 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5",
  },
  {
    title: "Skilling & Livelihood",
    subtitle: "Employability & Entrepreneurship",
    color: "#F59C00",
    items: ["Enhancing employability & entrepreneurship", "Structured training & mentoring", "Industry linkages for youth", "Livelihood opportunity creation"],
    icon: "M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0M12 12.75h.008v.008H12v-.008z",
  },
  {
    title: "Healthcare, Nutrition, Hygiene & Sanitation",
    subtitle: "Preventive & Community Health",
    color: "#DB6400",
    items: ["Promoting preventive healthcare", "Early screening & nutrition awareness", "Community health camps", "School-based interventions & policy dialogue"],
    icon: "M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z",
  },
  {
    title: "Environment & Sustainability",
    subtitle: "Climate Action & Stewardship",
    color: "#DB2628",
    items: ["Driving climate action & stewardship", "Renewable energy adoption", "Afforestation & pollution awareness", "Community-based sustainability initiatives"],
    icon: "M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z",
  },
  {
    title: "Diversity & Inclusion",
    subtitle: "Gender Equity & PwD Inclusion",
    color: "#DA2128",
    items: ["Fostering inclusive & equitable ecosystems", "Advancing gender equity", "Empowering women", "Enhancing participation of Persons with Disabilities (PwDs)"],
    icon: "M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z",
  },
];

function Pillars() {
  return (
    <Section id="pillars" bg="bg-white">
      <Orb color="#DA2128" size="450px" top="10%" left="5%" opacity="0.025" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="text-center mb-12">
          <SectionLabel>AFCSR Thematic Pillars</SectionLabel>
          <h2 className="font-fraunces text-[clamp(1.4rem,3vw,2.2rem)] font-[600] leading-[1.15] text-gray-900 mt-4">
            Anchored Around{" "}
            <span className="text-transparent bg-clip-text" style={{ backgroundImage: "linear-gradient(135deg, #DA2128, #DB6400)" }}>Five Core Pillars</span>
          </h2>
          <p className="text-gray-500 mt-3 max-w-2xl mx-auto text-sm leading-relaxed">
            Our work and initiatives are aligned with national priorities, driving measurable impact
            across India&apos;s most critical social and environmental challenges.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {pillars.map((p, idx) => (
            <PillarCard key={p.title} pillar={p} index={idx} />
          ))}
        </div>
      </div>
    </Section>
  );
}

function PillarCard({ pillar, index }) {
  const [ref, inView] = useInView(0.08);
  return (
    <div
      ref={ref}
      className={`group relative bg-white rounded-[1.5rem] p-6 shadow-lg border border-gray-100/80 transition-all duration-700 ease-out ${
        inView ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"
      } hover:shadow-2xl hover:-translate-y-2`}
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      <div className="absolute top-0 left-5 right-5 h-[2px] rounded-full transition-all duration-500 group-hover:scale-x-105 origin-left" style={{ background: `linear-gradient(90deg, ${pillar.color}, ${pillar.color}66)` }} />

      <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-4 shadow-md transition-all duration-300 group-hover:scale-110 group-hover:shadow-xl" style={{ background: `linear-gradient(135deg, ${pillar.color}, ${pillar.color}bb)` }}>
        <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d={pillar.icon} />
        </svg>
      </div>

      <h3 className="font-fraunces text-base font-[600] text-gray-900 mb-0.5">{pillar.title}</h3>
      <p className="text-[11px] font-medium mb-3" style={{ color: pillar.color }}>{pillar.subtitle}</p>

      <ul className="space-y-2">
        {pillar.items.map((item) => (
          <li key={item} className="flex items-start gap-2 text-xs text-gray-600">
            <span className="mt-1.5 w-[5px] h-[5px] rounded-full flex-shrink-0" style={{ background: pillar.color }} />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  LEADERSHIP
// ═══════════════════════════════════════════════════════════════════════════
function Leadership() {
  const leaders = [
    { name: "Shri. Vineet Jain", role: "Managing Trustee", desc: "Leading the vision of AFCSR with decades of experience in policy advocacy and corporate governance." },
    { name: "Shri. Rajesh Gupta", role: "Trustee", desc: "Driving strategic partnerships and programmatic excellence across the foundation's thematic areas." },
    { name: "Dr. Neeta Sharma", role: "Trustee", desc: "Bringing deep expertise in education, skilling, and community development initiatives." },
    { name: "Shri. Amit Kapoor", role: "CEO", desc: "Overseeing day-to-day operations and ensuring impactful delivery of CSR programmes nationwide." },
  ];

  return (
    <Section id="leadership" bg="bg-[#FAF7F2]">
      <Orb color="#F59C00" size="400px" top="80%" left="85%" opacity="0.04" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="text-center mb-12">
          <SectionLabel>Foundation Leadership</SectionLabel>
          <h2 className="font-fraunces text-[clamp(1.4rem,3vw,2.2rem)] font-[600] leading-[1.15] text-gray-900 mt-4">
            Our <span className="text-transparent bg-clip-text" style={{ backgroundImage: "linear-gradient(135deg, #DA2128, #DB6400)" }}>Leadership Team</span>
          </h2>
          <p className="text-gray-500 mt-3 text-sm leading-relaxed">
            Guided by experienced leaders committed to driving social impact and sustainable change.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {leaders.map((l) => (
            <div
              key={l.name}
              className="group relative bg-white rounded-[1.5rem] p-5 lg:p-6 shadow-lg transition-all duration-500 hover:-translate-y-3 hover:shadow-2xl text-center"
            >
              <div className="relative mx-auto mb-4">
                <div className="w-[72px] h-[72px] rounded-[1rem] flex items-center justify-center text-white text-2xl font-bold font-fraunces shadow-lg transition-all duration-300 group-hover:scale-105 group-hover:shadow-xl" style={{ background: "linear-gradient(135deg, #DA2128, #DB6400)" }}>
                  {l.name.split(" ").pop()?.[0] || "L"}
                </div>
                <div className="absolute -bottom-1 -right-1 w-[18px] h-[18px] rounded-full bg-[#F59C00] border-[2px] border-white transition-transform duration-300 group-hover:scale-110" />
              </div>
              <h4 className="font-fraunces font-[600] text-sm text-gray-900">{l.name}</h4>
              <p className="text-[11px] font-medium mt-1" style={{ color: "#DA2128" }}>{l.role}</p>
              <p className="text-[11px] text-gray-500 mt-2 leading-relaxed">{l.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}



// ═══════════════════════════════════════════════════════════════════════════
//  MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════
export default function AFCSR() {
  return (
    <div className="min-h-screen" style={{ background: "#FFFFFF" }}>

      <Hero />
      <Overview />
      <VisionMission />
      <Pillars />
      <Leadership />

    </div>
  );
}
